/**
 * Maps the current app path to a human-readable module label for activity tracking.
 * Returns null for public/auth routes where heartbeat should be skipped.
 */
const MODULE_RULES: Array<{ prefix: string; label: string }> = [
  { prefix: "/admin/dashboard", label: "Admin · Dashboard" },
  { prefix: "/admin/support", label: "Admin · Soporte" },
  { prefix: "/admin/sendgrid-billing", label: "Admin · Facturación" },
  { prefix: "/social/dashboard", label: "Inicio" },
  { prefix: "/social/streams", label: "Publicaciones · Muro" },
  { prefix: "/social/post", label: "Publicaciones · Crear post" },
  { prefix: "/social/story", label: "Publicaciones · Historia" },
  { prefix: "/social/history", label: "Publicaciones · Historial" },
  { prefix: "/social/manager", label: "Publicaciones · Gestor" },
  { prefix: "/social/content-grid", label: "Publicaciones · Grilla" },
  { prefix: "/social/calendar", label: "Publicaciones · Calendario" },
  { prefix: "/social/automated-posts", label: "Publicaciones · Automatizadas" },
  { prefix: "/social/ai-generation", label: "IA · Generación" },
  { prefix: "/social/ai/text-to-image", label: "IA · Texto a imagen" },
  { prefix: "/ia/video-gen", label: "IA · Video" },
  { prefix: "/ia/editor-videos", label: "IA · Editor de video" },
  { prefix: "/social", label: "Publicaciones" },
  { prefix: "/analytics/resumen", label: "Analytics · Resumen" },
  { prefix: "/analytics/facebook", label: "Analytics · Facebook" },
  { prefix: "/analytics/instagram", label: "Analytics · Instagram" },
  { prefix: "/analytics/tiktok", label: "Analytics · TikTok" },
  { prefix: "/analytics/linkedin-business", label: "Analytics · LinkedIn" },
  { prefix: "/analytics/youtube", label: "Analytics · YouTube" },
  { prefix: "/analytics/google", label: "Analytics · Google" },
  { prefix: "/analytics/woocommerce", label: "Analytics · WooCommerce" },
  { prefix: "/analytics/mercadolibre", label: "Analytics · Mercado Libre" },
  { prefix: "/analytics", label: "Analytics" },
  { prefix: "/chat/web", label: "Chat · Web" },
  { prefix: "/chat/whatsapp", label: "Chat · WhatsApp" },
  { prefix: "/chat/message-scheduling", label: "Chat · Programación de mensaje" },
  { prefix: "/contacts", label: "CRM · Contactos" },
  { prefix: "/companies/contact", label: "CRM · Empresas" },
  { prefix: "/boards/activity", label: "Tableros · Actividad" },
  { prefix: "/boards/business", label: "Tableros · Negocios" },
  { prefix: "/boards/tasks", label: "Tableros · Tareas" },
  { prefix: "/boards", label: "Tableros" },
  { prefix: "/form/web", label: "Formularios · Web" },
  { prefix: "/form/facebook", label: "Formularios · Facebook" },
  { prefix: "/reports", label: "Reportes" },
  { prefix: "/market/woocommerce", label: "Market · WooCommerce" },
  { prefix: "/market/mercadolibre", label: "Market · Mercado Libre" },
  { prefix: "/market/jumpseller", label: "Market · Jumpseller" },
  { prefix: "/facebook/campaigns", label: "Campañas · Facebook" },
  { prefix: "/campaign", label: "Campañas" },
  { prefix: "/emails", label: "Email marketing" },
  { prefix: "/users", label: "Usuarios" },
  { prefix: "/company", label: "Empresa" },
  { prefix: "/me/company", label: "Mi empresa" },
  { prefix: "/plans", label: "Planes" },
  { prefix: "/resellers", label: "Revendedores" },
  { prefix: "/binding", label: "Integraciones" },
];

const SKIP_PREFIXES = [
  "/login",
  "/create_account",
  "/forgot_password",
  "/auth/",
  "/meeting/",
  "/webform/",
  "/public/",
];

const INTEGRATION_POPUP_PREFIXES = [
  "/social/linkedin",
  "/social/twitter",
  "/social/tiktok",
  "/social/google",
  "/social/instagram-login",
  "/social/youtube",
  "/social/gmail",
  "/social/office365",
  "/social/mercadolibre",
  "/social/jumpseller",
  "/social/mailchimp",
  "/social/woo",
];

function normalizePath(pathname: string) {
  const base = pathname.split("?")[0].split("#")[0];
  if (!base || base === "/") return "/";
  return base.endsWith("/") && base.length > 1 ? base.slice(0, -1) : base;
}

export function shouldTrackActivityPath(pathname: string) {
  const path = normalizePath(pathname);
  if (SKIP_PREFIXES.some((prefix) => path.startsWith(prefix))) return false;
  if (INTEGRATION_POPUP_PREFIXES.some((prefix) => path.startsWith(prefix))) {
    return false;
  }
  return true;
}

export function resolveActivityModule(pathname: string): string | null {
  const path = normalizePath(pathname);
  if (!shouldTrackActivityPath(path)) return null;

  if (INTEGRATION_POPUP_PREFIXES.some((prefix) => path.startsWith(prefix))) {
    return "Integraciones";
  }

  const match = MODULE_RULES.find((rule) => path.startsWith(rule.prefix));
  if (match) return match.label;

  if (path.startsWith("/social")) return "Publicaciones";
  if (path.startsWith("/admin")) return "Administración";

  return "Wembii";
}
